import { Button } from "@/components/ui";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Check, Copy, Edit, Reply, Share } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { getContentWithSources } from "../utils/citationFormatter";
import { ModelSwitchAndRerun } from "./";

/**
 * MessageActions component - Action buttons for messages (copy, edit, share, etc.)
 * Simplified from ActionButtons.jsx with cleaner prop interface
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isUser - Whether this is a user message
 * @param {Object} props.message - The message object
 * @param {string} props.content - Message content
 * @param {string} props.displayContent - Formatted display content
 * @param {boolean} props.isStreaming - Whether message is streaming
 * @param {boolean} props.showButtons - Whether to show buttons (hover state)
 * @param {boolean} props.hasToolCalls - Whether message has tool calls
 * @param {boolean} props.hasCompletedToolCalls - Whether tool calls completed
 * @param {boolean} props.hasFileAttachments - Whether message has file attachments
 * @param {Object} props.toolData - Tool data (firecrawl, dalle, canvas)
 * @param {function} props.onCopy - Copy handler
 * @param {function} props.onEdit - Edit handler (user messages)
 * @param {function} props.onShare - Share handler (assistant messages)
 * @param {function} props.onRerun - Model switch & rerun handler (user messages)
 * @param {function} props.onReply - Reply handler
 * @param {function} props.onSourcesClick - Sources click handler
 * @param {function} props.onImageClick - Image click handler
 * @param {function} props.onCanvasClick - Canvas click handler
 * @param {boolean} props.shareButtonEnabled - Whether share button is enabled
 * @param {string} props.className - Additional CSS classes
 */
const MessageActions = ({
  isUser,
  message,
  content = "",
  displayContent = "",
  isStreaming = false,
  showButtons = false,
  hasToolCalls = false,
  hasCompletedToolCalls = false,
  hasFileAttachments = false,
  toolData = {},
  onCopy,
  onEdit = null,
  onShare = null,
  onRerun = null,
  onReply = null,
  onSourcesClick = null,
  _onImageClick = null, // Prefixed with _ to indicate unused (kept for future use)
  onCanvasClick = null,
  shareButtonEnabled = false,
  replyButtonEnabled = false, // Disabled by default - TODO: fix and re-enable
  className = "",
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setCopySuccess(false);
  }, [displayContent, message?.id]);

  const handleCopyClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    let contentToCopy = displayContent || "";

    // Determine effective canvas data (current or previous message)
    const effectiveCanvasData =
      toolData?.canvasData || toolData?.previousMessageCanvasData;

    // If there's canvas data, handle different scenarios
    if (effectiveCanvasData && effectiveCanvasData.content) {
      const canvasContent = effectiveCanvasData.content;
      const hasTextContent = contentToCopy && contentToCopy.trim() !== "";

      if (hasTextContent) {
        // Both text and canvas: combine with separator
        contentToCopy = `${contentToCopy}\n\n---\n\n${canvasContent}`;
      } else {
        // Canvas only: just copy canvas content
        contentToCopy = canvasContent;
      }
    } else if (toolData?.firecrawlSearchData && !isUser) {
      // Include sources in copied text if available (for MLA citations)
      try {
        contentToCopy = getContentWithSources(
          displayContent,
          toolData.firecrawlSearchData,
          true
        );
      } catch (error) {
        console.error(
          "[MessageActions] Error getting content with sources:",
          error
        );
        contentToCopy = displayContent || "";
      }
    }

    onCopy(contentToCopy);
    setCopySuccess(true);
    if (copyTimeoutRef.current) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    if (typeof window !== "undefined") {
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    }
  };

  // For assistant messages, hide buttons if there are tool calls but no meaningful content
  // UNLESS there's a canvas (canvas-only messages should show action buttons)
  const hasCanvasData =
    toolData?.canvasData || toolData?.previousMessageCanvasData;
  if (
    !isUser &&
    hasToolCalls &&
    (!content || content.trim() === "" || content.trim() === " ") &&
    !hasCanvasData
  ) {
    return null;
  }

  const copyIcon = copySuccess ? (
    <Check className="h-3 w-3 text-emerald-500" />
  ) : (
    <Copy className="h-3 w-3 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
  );

  // Determine copy tooltip text based on what will be copied
  const hasTextContent =
    content && content.trim() !== "" && content.trim() !== " ";
  const hasCanvasContent =
    hasCanvasData && (hasCanvasData.content || "").trim() !== "";

  let copyTooltipText = "Copy message";
  if (copySuccess) {
    copyTooltipText = "Copied!";
  } else if (hasTextContent && hasCanvasContent) {
    copyTooltipText = "Copy message + canvas";
  } else if (!hasTextContent && hasCanvasContent) {
    copyTooltipText = "Copy canvas content";
  }

  // Assistant message buttons
  if (!isUser) {
    // Determine effective tool data (current or previous message)
    const hasCompletedToolCallsWithResults =
      hasToolCalls && hasCompletedToolCalls;
    const shouldShowCurrentToolData =
      !hasToolCalls || hasCompletedToolCallsWithResults;

    const effectiveFirecrawlData =
      (shouldShowCurrentToolData ? toolData?.firecrawlSearchData : null) ||
      toolData?.previousMessageFirecrawlData;
    const effectiveDalleData =
      (shouldShowCurrentToolData ? toolData?.dalleImageData : null) ||
      toolData?.previousMessageImageData;
    const effectiveCanvasData =
      (shouldShowCurrentToolData ? toolData?.canvasData : null) ||
      toolData?.previousMessageCanvasData;
    const effectiveHasToolData =
      effectiveFirecrawlData || effectiveDalleData || effectiveCanvasData;

    const shouldShowAssistantButtons =
      !isStreaming || hasCompletedToolCalls || (content && content.trim());

    // If there's effective tool data, always show buttons (no hover needed)
    const shouldShowButtons = effectiveHasToolData ? true : showButtons;

    return (
      <div
        className={cn(
          // Simplified positioning - relative within grid
          "flex items-center gap-1.5 mt-2",
          "transition-opacity duration-300 ease-in-out",
          shouldShowAssistantButtons && shouldShowButtons
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
          className
        )}
        data-message-id={message.id}
      >
        {shareButtonEnabled && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShare();
                  }}
                  disabled={isStreaming}
                  className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
                  data-prevent-scroll="true"
                >
                  <Share className="h-4 w-4 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
                  <span className="sr-only">Share message</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Share message</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                onClick={handleCopyClick}
                disabled={isStreaming}
                className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
                data-prevent-scroll="true"
              >
                {copyIcon}
                <span className="sr-only">
                  {copySuccess ? "Copied" : "Copy message"}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{copyTooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Reply button - rightmost position */}
        {replyButtonEnabled && onReply && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onReply(message);
                  }}
                  disabled={isStreaming}
                  className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
                  data-prevent-scroll="true"
                >
                  <Reply className="h-4 w-4 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
                  <span className="sr-only">Reply to message</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Reply to message</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  // User message buttons
  if (isUser && !isStreaming) {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 mt-2 justify-end",
          "transition-opacity duration-300 ease-in-out",
          showButtons
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
          className
        )}
      >
        <ModelSwitchAndRerun
          message={message}
          content={content}
          onRerun={onRerun}
        />

        {!hasFileAttachments && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={onEdit}
                  onMouseEnter={(e) => e.stopPropagation()}
                  className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
                  data-prevent-scroll="true"
                >
                  <Edit className="h-4 w-4 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
                  <span className="sr-only">Edit</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Edit message</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                onClick={handleCopyClick}
                onMouseEnter={(e) => e.stopPropagation()}
                className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
                data-prevent-scroll="true"
              >
                {copyIcon}
                <span className="sr-only">
                  {copySuccess ? "Copied" : "Copy message"}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{copyTooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Reply button - rightmost position */}
        {replyButtonEnabled && onReply && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onReply(message);
                  }}
                  onMouseEnter={(e) => e.stopPropagation()}
                  className="h-8 w-8 rounded-md bg-transparent border-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group"
                  data-prevent-scroll="true"
                >
                  <Reply className="h-4 w-4 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
                  <span className="sr-only">Reply to message</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Reply to message</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  return null;
};

MessageActions.propTypes = {
  isUser: PropTypes.bool.isRequired,
  message: PropTypes.object.isRequired,
  content: PropTypes.string,
  displayContent: PropTypes.string,
  isStreaming: PropTypes.bool,
  showButtons: PropTypes.bool,
  hasToolCalls: PropTypes.bool,
  hasCompletedToolCalls: PropTypes.bool,
  hasFileAttachments: PropTypes.bool,
  toolData: PropTypes.shape({
    firecrawlSearchData: PropTypes.object,
    dalleImageData: PropTypes.object,
    canvasData: PropTypes.object,
    previousMessageHasDeepResearch: PropTypes.bool,
    previousMessageFirecrawlData: PropTypes.object,
    previousMessageImageData: PropTypes.object,
    previousMessageCanvasData: PropTypes.object,
  }),
  onCopy: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onShare: PropTypes.func,
  onRerun: PropTypes.func,
  onSourcesClick: PropTypes.func,
  _onImageClick: PropTypes.func, // Prefixed with _ to indicate unused
  onCanvasClick: PropTypes.func,
  shareButtonEnabled: PropTypes.bool,
  replyButtonEnabled: PropTypes.bool,
  className: PropTypes.string,
};

export default MessageActions;
